// /src/pages/SplashPage.jsx
/* eslint-disable */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { imageDB } from "../utils/imageDB"; // splash 이미지: imageDB.splash
import { useAppConfig } from "../context/AppConfigContext";
import { getCurrentMember } from "../services/authService"; // 🔹 자동로그인용
import { flushPendingFcmTokenIfAny } from "services/pushTokenService";

const BRAND_LINE_1 = "DH mobility";
const BRAND_LINE_2 = "Hey’Way"; // 필요 없으면 "" 로

export default function SplashPage({
  letterDelay = 45,    // 글자당 지연(ms)
  letterDur = 520,     // 글자 애니메이션 시간(ms)
  stayAfter = 600,     // 마지막 글자 이후 대기(ms)
  bgOverride,          // 배경 컬러 오버라이드
  showText = false,    // 텍스트 노출 여부 (기본 off)
  extraHold = 3000,    // 스플래시를 조금 더 보여줄 추가 대기(ms)
}) {
  const nav = useNavigate();
  const loc = useLocation();
  const { config } = useAppConfig();
  const splashImg = config.splash?.img || imageDB.splash;

  const [leaving, setLeaving] = useState(false);
  const firedRef = useRef(false);      // StrictMode 중복 실행 방지
  const timersRef = useRef([]);        // 타이머 취소용

  // 텍스트 라인 구성
  const lines = useMemo(() => [BRAND_LINE_1, BRAND_LINE_2].filter(Boolean), []);
  const totalLetters = useMemo(() => lines.join("").length, [lines]);

  // 전체 러닝타임 계산 (텍스트 off이면 기본 모션 700ms)
  const totalRuntime = useMemo(() => {
    const base = showText ? totalLetters * letterDelay + letterDur : 700;
    return base + stayAfter + extraHold;
  }, [showText, totalLetters, letterDelay, letterDur, stayAfter, extraHold]);

  // RN WebView white flash 방지 신호
  useEffect(() => {
    const postReady = () => {
      if (window.ReactNativeWebView) {
        try {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: "WEB_READY",
              payload: { stage: "splash-mounted", at: Date.now() },
            })
          );
        } catch (_) { }
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(postReady));
  }, []);

  // ✅ 자동로그인 + 페이드아웃 + 라우팅
  useEffect(() => {
    if (firedRef.current) return; // 중복 예약 방지
    firedRef.current = true;

    let cancelled = false;

    (async () => {
      let targetPath = "/loginlanding"; // 기본: 로그인 랜딩

      try {
        // 🔹 자동로그인 키(phoneE164)로 현재 멤버 조회
        const member = await getCurrentMember();
        if (member) {
          console.log("[Splash] auto login success for", member.username, member.phoneE164);

          try {
            await flushPendingFcmTokenIfAny({ phoneE164: member.phoneE164 });
          } catch (eFlush) {
            console.warn("[Splash] flushPendingFcmToken failed", eFlush);
          }

          targetPath = "/home";
        } else {
          console.log("[Splash] no saved member → go loginlanding");
        }
      } catch (e) {
        console.error("[Splash] auto login error", e);
      }

      const fadeMs = 260;
      const t1 = setTimeout(
        () => !cancelled && setLeaving(true),
        Math.max(0, totalRuntime - fadeMs)
      );
      const t2 = setTimeout(() => {
        if (cancelled) return;
        nav(targetPath, { replace: true });
      }, totalRuntime);

      timersRef.current = [t1, t2];
    })();

    return () => {
      cancelled = true;
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
  }, [nav, totalRuntime, loc.pathname]);

  return (
    <Wrap $bg={bgOverride} $leaving={leaving}>
      {/* 중앙 스플래시 이미지 (풀스크린) */}
      <Mascot
        src={splashImg}
        alt="DH Mobility splash"
        draggable={false}
      />

      {/* (옵션) 텍스트 애니메이션도 함께 노출하려면 showText=true */}
      {showText && (
        <BrandBlock>
          {lines.map((text, lineIdx) => (
            <Line key={lineIdx} $lineIdx={lineIdx}>
              {text.split("").map((ch, i) => {
                const char = ch === " " ? "\u00A0" : ch;
                const delay = i * letterDelay + lineIdx * 120; // 줄마다 오프셋
                return (
                  <Letter
                    key={`${lineIdx}-${i}`}
                    style={{
                      animationDelay: `${delay}ms`,
                      animationDuration: `${letterDur}ms`,
                    }}
                  >
                    {char}
                  </Letter>
                );
              })}
            </Line>
          ))}
          <Caption>Powered by DH mobility</Caption>
        </BrandBlock>
      )}
    </Wrap>
  );
}

/* ===== styled-components ===== */

const popIn = keyframes`
  0%   { opacity: 0; transform: translateY(12px) scale(.92); filter: blur(2px); }
  60%  { opacity: 1; transform: translateY(-2px) scale(1.02); filter: blur(.2px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;
const flyIn = keyframes`
  0% { opacity: 0; transform: translate3d(0,28px,-140px) scale(.92) rotateX(18deg); filter: blur(2.5px); }
  60% { opacity: 1; transform: translate3d(0,-2px,12px)  scale(1.02) rotateX(0);   filter: blur(.2px); }
  100%{ opacity: 1; transform: translate3d(0,0,0)        scale(1)    rotateX(0);   filter: blur(0); }
`;

const Wrap = styled.div`
  height: 100dvh;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: #fff;
  display: block;
  transition: opacity 260ms ease, transform 260ms ease, filter 260ms ease;
  opacity: ${({ $leaving }) => ($leaving ? 0 : 1)};
  transform: ${({ $leaving }) => ($leaving ? "scale(0.985)" : "scale(1)")};
  filter: ${({ $leaving }) => ($leaving ? "blur(1px)" : "none")};
`;

const Mascot = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  animation: ${popIn} 560ms cubic-bezier(.19,.74,.32,1) both;
  user-select: none;
  pointer-events: none;
`;

const BrandBlock = styled.div`
  position: absolute;
  bottom: 8vh;
  text-align: center;
  line-height: 1.1;
`;
const Line = styled.div`
  display: inline-block;
  transform-style: preserve-3d;
  margin-bottom: ${({ $lineIdx }) => ($lineIdx === 0 ? "6px" : "0")};
  font-size: ${({ $lineIdx }) => ($lineIdx === 0 ? 28 : 20)}px;
  font-weight: ${({ $lineIdx }) => ($lineIdx === 0 ? 800 : 700)};
  letter-spacing: ${({ $lineIdx }) => ($lineIdx === 0 ? 0.2 : 0.15)}em;
`;
const Letter = styled.span`
  display: inline-block;
  opacity: 0;
  transform: translate3d(0,28px,-140px) scale(.92) rotateX(18deg);
  filter: blur(2px);
  animation-name: ${flyIn};
  animation-fill-mode: forwards;
  animation-timing-function: cubic-bezier(.19,.74,.32,1);
`;
const Caption = styled.div`
  margin-top: 16px;
  font-size: 12px;
  color: var(--color-muted);
  opacity: 0.9;
`;
